import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from '@/components/ui/shadcn/dropdown-menu';
import { Input } from '@/components/ui/shadcn/input';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, ArrowUpDown, EyeOff, MoreHorizontal, Tag, X } from 'lucide-react';
import { useState } from 'react';

interface TableColumnHeaderProps {
    label: string;
    sortDirection?: 'asc' | 'desc' | null;
    onSortAscending?: () => void;
    onSortDescending?: () => void;
    onMoveLeft?: () => void;
    onMoveRight?: () => void;
    onEditLabel?: () => void;
    onHide?: () => void;
    canMoveLeft?: boolean;
    canMoveRight?: boolean;
    className?: string;
    filterOptions?: Array<{ value: string; label: string }>;
    selectedFilters?: string[];
    onFilterChange?: (values: string[]) => void;
    isFiltered?: boolean;
}

export function TableColumnHeader({
    label,
    sortDirection,
    onSortAscending,
    onSortDescending,
    onMoveLeft,
    onMoveRight,
    onEditLabel,
    onHide,
    canMoveLeft = true,
    canMoveRight = true,
    className,
    filterOptions = [],
    selectedFilters = [],
    onFilterChange,
    isFiltered = false,
}: TableColumnHeaderProps) {
    const { t } = useTranslations();
    const [isOpen, setIsOpen] = useState(false);
    const [filterSearch, setFilterSearch] = useState('');

    const showSearch = filterOptions.length > 3;
    const filteredOptions = showSearch
        ? filterOptions.filter((option) => option.label.toLowerCase().includes(filterSearch.toLowerCase()))
        : filterOptions;

    const isSortable = Boolean(onSortAscending || onSortDescending);

    // Ascending first, then descending on a second click — the same two actions
    // the menu offers, just reachable in one click.
    const toggleSort = () => {
        if (sortDirection === 'asc') {
            (onSortDescending ?? onSortAscending)?.();
            return;
        }
        (onSortAscending ?? onSortDescending)?.();
    };

    const sortActionLabel =
        sortDirection === 'asc'
            ? t('work_orders.table_actions.sort_descending', 'Sort descending')
            : t('work_orders.table_actions.sort_ascending', 'Sort ascending');

    const sortIndicator = sortDirection ? (
        <span className="text-muted-foreground shrink-0">
            {sortDirection === 'asc' ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />}
        </span>
    ) : isSortable ? (
        // Hint that the column is clickable without competing with the active state.
        <ArrowUpDown className="text-muted-foreground h-3.5 w-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-60" />
    ) : null;

    return (
        <DropdownMenu
            open={isOpen}
            onOpenChange={(open) => {
                setIsOpen(open);
                if (!open) {
                    setFilterSearch('');
                }
            }}
        >
            <div className={cn('hover:bg-accent group flex w-full items-center gap-1 rounded transition-colors', className)}>
                {/* Clicking the label sorts directly — the conventional interaction.
                    Columns with no sort handlers fall back to opening the menu so
                    the label stays clickable rather than looking inert. */}
                {isSortable ? (
                    <button
                        type="button"
                        onClick={toggleSort}
                        aria-label={sortActionLabel}
                        title={sortActionLabel}
                        className="flex min-w-0 flex-1 items-center gap-2 px-3 py-2 text-left text-sm font-medium"
                    >
                        <span className="truncate">{label}</span>
                        {sortIndicator}
                        {isFiltered && <span className="bg-primary h-2 w-2 shrink-0 rounded-full" aria-hidden />}
                    </button>
                ) : (
                    <DropdownMenuTrigger asChild>
                        <button type="button" className="flex min-w-0 flex-1 items-center gap-2 px-3 py-2 text-left text-sm font-medium">
                            <span className="truncate">{label}</span>
                            {sortIndicator}
                            {isFiltered && <span className="bg-primary h-2 w-2 shrink-0 rounded-full" aria-hidden />}
                        </button>
                    </DropdownMenuTrigger>
                )}
                <DropdownMenuTrigger asChild>
                    <button
                        type="button"
                        aria-label={t('work_orders.table_actions.column_options', 'Column options')}
                        title={t('work_orders.table_actions.column_options', 'Column options')}
                        className={cn(
                            'shrink-0 rounded p-1.5 transition-opacity',
                            'text-muted-foreground hover:bg-accent',
                            isOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
                        )}
                    >
                        <MoreHorizontal className="h-4 w-4" />
                    </button>
                </DropdownMenuTrigger>
            </div>
            <DropdownMenuContent align="start">
                {onSortAscending && (
                    <DropdownMenuItem onClick={onSortAscending} className="cursor-pointer">
                        <ArrowUp className="mr-2 h-4 w-4" />
                        {t('work_orders.table_actions.sort_ascending', 'Sort ascending')}
                    </DropdownMenuItem>
                )}
                {onSortDescending && (
                    <DropdownMenuItem onClick={onSortDescending} className="cursor-pointer">
                        <ArrowDown className="mr-2 h-4 w-4" />
                        {t('work_orders.table_actions.sort_descending', 'Sort descending')}
                    </DropdownMenuItem>
                )}
                {(onMoveLeft || onMoveRight) && (
                    <>
                        {onMoveLeft && canMoveLeft && (
                            <DropdownMenuItem onClick={onMoveLeft} className="cursor-pointer">
                                <ArrowLeft className="mr-2 h-4 w-4" />
                                {t('work_orders.table_actions.move_left', 'Move left')}
                            </DropdownMenuItem>
                        )}
                        {onMoveRight && canMoveRight && (
                            <DropdownMenuItem onClick={onMoveRight} className="cursor-pointer">
                                <ArrowRight className="mr-2 h-4 w-4" />
                                {t('work_orders.table_actions.move_right', 'Move right')}
                            </DropdownMenuItem>
                        )}
                    </>
                )}
                {onEditLabel && (
                    <DropdownMenuItem onClick={onEditLabel} className="cursor-pointer">
                        <Tag className="mr-2 h-4 w-4" />
                        {t('work_orders.table_actions.edit_column_label', 'Edit column label')}
                    </DropdownMenuItem>
                )}
                {onHide && (
                    <DropdownMenuItem onClick={onHide} className="cursor-pointer">
                        <EyeOff className="mr-2 h-4 w-4" />
                        {t('work_orders.table_actions.hide_from_view', 'Hide from view')}
                    </DropdownMenuItem>
                )}
                {filterOptions.length > 0 && (
                    <>
                        <DropdownMenuSeparator />
                        <DropdownMenuSub>
                            <DropdownMenuSubTrigger>
                                <span className="flex items-center gap-2">
                                    <span>{t('work_orders.table_actions.filter_by', 'Filter by')}</span>
                                    {isFiltered && <span className="bg-primary h-2 w-2 rounded-full" aria-hidden />}
                                </span>
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent className="w-56">
                                {showSearch && (
                                    <div className="px-2 pb-2">
                                        <Input
                                            autoFocus
                                            placeholder={t('work_orders.table_actions.search_options', 'Search options...')}
                                            value={filterSearch}
                                            onChange={(e) => setFilterSearch(e.target.value)}
                                        />
                                    </div>
                                )}
                                {filteredOptions.length === 0 ? (
                                    <DropdownMenuItem className="text-muted-foreground cursor-default" disabled>
                                        {t('work_orders.table_actions.no_matches', 'No matches')}
                                    </DropdownMenuItem>
                                ) : (
                                    filteredOptions.map((option) => {
                                        const checked = selectedFilters.includes(option.value);
                                        return (
                                            <DropdownMenuCheckboxItem
                                                key={option.value || 'empty-option'}
                                                className="cursor-pointer"
                                                checked={checked}
                                                onCheckedChange={(next) => {
                                                    if (!onFilterChange) return;
                                                    const nextValues = new Set(selectedFilters);
                                                    if (next) {
                                                        nextValues.add(option.value);
                                                    } else {
                                                        nextValues.delete(option.value);
                                                    }
                                                    onFilterChange(Array.from(nextValues));
                                                }}
                                                onSelect={(event) => event.preventDefault()}
                                            >
                                                <span className="truncate text-sm">{option.label || '—'}</span>
                                            </DropdownMenuCheckboxItem>
                                        );
                                    })
                                )}
                                {selectedFilters.length > 0 && (
                                    <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem onClick={() => onFilterChange?.([])} className="text-destructive cursor-pointer">
                                            <X className="mr-2 h-4 w-4" />
                                            {t('work_orders.table_actions.clear_filter', 'Clear filter')}
                                        </DropdownMenuItem>
                                    </>
                                )}
                            </DropdownMenuSubContent>
                        </DropdownMenuSub>
                    </>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
